<template>
  <div class="admission-note-container">
    <div class="document-container">
      <validation-observer ref="interventionPlanDocument">
        <div v-if="data" class="row align-items-center">
          <div class="col-12 col-md-10">
            <label class="input-display d-block">
              Nota de admisión firmada:
            </label>
            <span v-if="data" class="text-format">{{ data.fileName }}</span>
          </div>
          <div v-if="!readOnly" class="col-12 col-md-2 text-right">
            <action-button
              v-if="canReplaceAdmissionNote"
              id="replace-admission-note-button"
              i="fas fa-undo text-green"
              variant="btn border-green bg-white mr-2"
              @click="onReplaceAdmissionNote"
            />
            <action-button
              v-if="canDownloadAdmissionNote"
              id="download-admission-note-button"
              i="fas fa-file-download text-white"
              variant="btn mr-2 bg-green"
              @click="downloadFile"
            />
            <input
              v-if="canReplaceAdmissionNote"
              v-show="false"
              id="admissionNoteHidden"
              ref="admissionNoteRef"
              :accept="allowedFiles"
              type="file"
              @change="updateFileChange($event)"
            />
          </div>
        </div>
        <div v-else class="row">
          <div class="col-md-6 pt-0">
            <label class="input-display d-block">
              Documento:
            </label>
            <span class="text-format">Admisión Firmada</span>
          </div>
          <div class="col-md-6">
            <validation-provider
              v-slot="{ errors }"
              name="Archivo"
              rules="required"
            >
              <custom-file-input
                ref="attachedDocument"
                v-model="document"
                :accept="allowedFiles"
                custom-error-msg="Favor de anejar el documento."
                display-style="normal"
                :error="errors[0]"
                label="Archivo:"
                label-position="left"
                required
              />
            </validation-provider>
          </div>
          <div class="col-12 mt-3 mt-md-2 d-flex justify-content-end">
            <action-button
              i="fa fa-file-plus"
              text="Guardar"
              variant="success"
              @click="saveDocumentHandler"
            />
          </div>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomFileInput from '@/components/common/CustomFileInput';
import alertMixin from 'utils/mixins/alert.mixin';
import {
  getSignedDocumentByStudentServiceId as _getSignedDocumentByStudentServiceId,
  saveThrpyReferralItemAttachment as _saveThrpyReferralItemAttachment,
} from '@/modules/shared/studentreferral.api';
import _generalApi from '@/modules/app/api';
import documentTypes from '@/utils/constants/documentTypes';

export default {
  name: 'AdmissionNoteDocument',
  components: {
    ActionButton,
    CustomFileInput,
  },
  mixins: [alertMixin],
  props: {
    readOnly: {
      type: Boolean,
      default: () => false,
    },
    studentServiceId: {
      type: Number,
      default: () => 0,
    },
    admissionNoteId: {
      type: Number,
      default: () => 0,
    },
    admissionId: {
      type: Number,
      default: () => 0,
    },
  },
  data: () => ({
    document: null,
    data: null,
    allowedFiles: '.pdf',
  }),
  computed: {
    canDownloadAdmissionNote() {
      return this.data.documentId != null;
    },
    canReplaceAdmissionNote() {
      return this.userHasPermissions(
        'provider.referral.admissionnote.signeddocument.replace'
      );
    },
  },
  async mounted() {
    await this.getSignedDocumentByStudentServiceId(this.studentServiceId);
  },
  methods: {
    clearDocument() {
      this.document = null;
      this.data = null;

      if (this.$refs.attachedDocument?.clear) {
        this.$refs.attachedDocument.clear();
      }

      if (this.$refs.admissionNoteRef) {
        this.$refs.admissionNoteRef.value = null;
      }
    },
    hasDocument() {
      return this.data?.documentId != null;
    },
    async saveDocumentHandler() {
      const isValid = await this.$refs.interventionPlanDocument.validate();

      if (!isValid) {
        return;
      }

      await this.onSaveDocument();
    },
    async onSaveDocument() {
      let formData = new FormData();
      formData.append('attachment', this.document);
      formData.append('fileName', this.document.name);
      formData.append(
        'StudentServiceId',
        this.studentServiceId ? this.studentServiceId : 0
      );
      formData.append('documentTitle', this.document.name);
      formData.append(
        'peiAttachmentTypeCode',
        documentTypes.SignedAdmissionNote
      );
      formData.append('admissionId', this.admissionId);

      try {
        const res = await _saveThrpyReferralItemAttachment(formData);
        if (!res) return;
        this.$emit('documentSaved');
        this.ShowToast(
          'Proceso Exitoso',
          'El documento ha sido cargado exitosamente.',
          'success'
        );
        await this.getSignedDocumentByStudentServiceId(this.studentServiceId);
      } catch (error) {
        if (error && error.response && error.response.data) {
          self.ShowToast('', error.response.data.message, 'error');
        }
      }
    },
    async getSignedDocumentByStudentServiceId(studentServiceId) {
      if (studentServiceId) {
        await _getSignedDocumentByStudentServiceId(studentServiceId)
          .then((res) => {
            const document = res.data;
            this.data = document;
          })
          .catch((err) => {
            this.ShowToast('Error', err.response.data.message, 'error');
          });
      }
    },
    async downloadFile() {
      await _generalApi
        .downloadDocumentByAttachmentId(this.data.documentId)
        .catch(() => {
          this.ShowToast('', 'El documento no existe.', 'error');
        });
    },
    onReplaceAdmissionNote() {
      this.$refs.admissionNoteRef.click();
    },
    async updateFileChange(e) {
      if (!e.target.files.length) return;

      const selectedFiles = Array.from(e.target.files);
      this.document = selectedFiles[0];

      await this.onSaveDocument();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

.border-green {
  border-color: map-get($colors, 'green') !important;
}
.admission-note-container {
  padding: 1rem 0.5rem;
  border-radius: 0.35rem;
  background: map-get($colors, 'secondary-3');
  border-left: 0.25rem solid map-get($colors, 'primary-1') !important;
  margin-bottom: 10px;

  ::v-deep {
    .title-icon {
      color: map-get($colors, 'primary-1') !important;
    }
    .dot {
      background: map-get($colors, 'white') !important;
    }
  }
}
.document-container {
  padding: 0 0.8rem;
  ::v-deep &__text-area {
    .small-counter {
      font-size: 0.8rem;
      color: map-get($colors, 'gray-6');
    }
  }

  .input-display {
    font-size: 0.875rem;
    color: map-get($colors, 'gray-6');
    font-weight: bold;
  }

  .text-format {
    overflow-wrap: break-word;
    text-align: justify;
  }
}
</style>
